import { observer } from "mobx-react-lite";
import * as React from "react";
import * as styles from "./style.module.css";
import I18n from "lib/I18n";
import { Button, Form, Modal } from "react-bootstrap";
import { getStore } from "bundles/BigqueryQueryViewer/store";
import {
  ChatMessage,
  ToolKind,
  ToolUse,
  Role,
} from "bundles/BigqueryQueryViewer/store/chat";
import { CustomMarkdown } from "./markdown";
import { IDockviewPanelProps } from "dockview";
import { useEffect, useState } from "react";
import { BsArrowClockwise, BsRobot } from "react-icons/bs";
import { ToolUseComponent } from "./tools";
import { convertBytesToReadable } from "lib/converter";

interface DryrunResultMessageProps {
  message: ChatMessage;
}

const DryrunResultMessage = observer((props: DryrunResultMessageProps) => {
  const { message } = props;

  let scannedBytes = 0;
  let cost = 0;

  try {
    const parsed = JSON.parse(message.processedText);
    if (parsed.error) {
      return (
        <>
          <div className={styles.stepperLabel}>
            {I18n.t(`bigquery_query_viewer.label.dry_run_query`)}
          </div>
          <p>{`:${parsed.line}:${parsed.column}: ${parsed.error}`}</p>
        </>
      );
    }
    if (typeof parsed.data_scanned_in_bytes == "number") {
      scannedBytes = parsed.data_scanned_in_bytes;
    }
    if (typeof parsed.cost == "number") {
      cost = parsed.cost;
    }
  } catch (e) {
    console.error(e);
  }

  return (
    <>
      <div className={styles.stepperLabel}>
        {I18n.t(`bigquery_query_viewer.label.dry_run_query`)}
      </div>
      <p>
        {I18n.t("bigquery_query_viewer.messages.dryrun_result", {
          bytes: convertBytesToReadable(scannedBytes),
          cost: cost.toPrecision(3),
        })}
      </p>
    </>
  );
});

interface MessageContentProps {
  message: ChatMessage;
  toolResults: Map<string, string>;
  isFirstModel: boolean;
  isLastInGroup: boolean;
  isLastGroup: boolean;
  isRequesting: boolean;
  width: number;
  showThoughts: boolean;
  onToolResponse: (tool: ToolUse, response: unknown) => void;
  onRetry: (message: ChatMessage) => void;
  onToggleShowThoughts: () => void;
}

const MessageContent = observer((props: MessageContentProps) => {
  const {
    message,
    toolResults,
    isFirstModel,
    isLastInGroup,
    isLastGroup,
    isRequesting,
    width,
    showThoughts,
    onToolResponse,
    onRetry,
    onToggleShowThoughts,
  } = props;
  const isLast = isLastInGroup && isLastGroup;

  if (message.role == Role.tool) {
    return message.toolKind == ToolKind.dryrunQuery ? (
      <div className={styles.stepperItem}>
        <DryrunResultMessage message={message} />
      </div>
    ) : null;
  }

  return (
    <div className={styles.stepperItem}>
      {isFirstModel && message.thoughtsText ? (
        <div>
          <Button
            size="sm"
            variant="light"
            className={styles.thoughtButton}
            onClick={onToggleShowThoughts}
          >
            {showThoughts
              ? I18n.t("bigquery_query_viewer.label.hide_thoughts")
              : I18n.t("bigquery_query_viewer.label.show_thoughts")}
          </Button>
          {showThoughts && (
            <div className={styles.thoughts}>
              <CustomMarkdown value={message.thoughtsText} width={width} />
            </div>
          )}
        </div>
      ) : (
        showThoughts &&
        message.thoughtsText && (
          <div className={styles.thoughts}>
            <CustomMarkdown value={message.thoughtsText} width={width} />
          </div>
        )
      )}
      {message.processedText ? (
        <CustomMarkdown value={message.processedText} width={width} />
      ) : (
        !message.isCompleted && (
          <p>{I18n.t("bigquery_query_viewer.label.ai_thinking")}</p>
        )
      )}
      {message.toolUses.map((_) => (
        <ToolUseComponent
          toolUse={_}
          result={toolResults.get(_.callKey)}
          isLast={isLast}
          onToolResponse={onToolResponse}
        />
      ))}
      {isLast &&
        !isRequesting &&
        message.isCompleted &&
        message.role == Role.model && (
          <div className={styles.messageAction}>
            <div
              className={styles.messageActionButton}
              onClick={() => onRetry(message)}
            >
              <BsArrowClockwise />
            </div>
          </div>
        )}
    </div>
  );
});

interface MessageGroupProps {
  messages: ChatMessage[];
  isLastGroup: boolean;
  isRequesting: boolean;
  width: number;
  showThoughts: boolean;
  onToolResponse: (tool: ToolUse, response: unknown) => void;
  onRetry: (message: ChatMessage) => void;
  onToggleShowThoughts: () => void;
}

const userVisibleText = (message: ChatMessage) => {
  const selection = message.userSelection;
  switch (selection) {
    case "yes":
    case "no":
    case "ok":
    case "cancel":
      return I18n.t(`bigquery_query_viewer.label.${selection}`);
    default:
      return selection ?? message.processedText;
  }
};

const MessageGroup = observer((props: MessageGroupProps) => {
  const {
    messages,
    isLastGroup,
    isRequesting,
    width,
    showThoughts,
    onToolResponse,
    onRetry,
    onToggleShowThoughts,
  } = props;

  const first = messages[0];
  const isUserGroup = first.role == Role.user || first.isUserSelection;
  const toolResults = new Map(
    messages
      .filter((message) => message.role == Role.tool && message.callKey)
      .map((message) => [message.callKey, message.processedText]),
  );

  return (
    <div className={styles.messageGroup}>
      <div className={styles.messageRole}>
        {!isUserGroup && <BsRobot style={{ marginRight: 4 }} />}
        {I18n.t(
          `bigquery_query_viewer.label.roles_${isUserGroup ? Role.user : Role.model}`,
        )}
      </div>
      {isUserGroup ? (
        <CustomMarkdown value={userVisibleText(first)} width={width} />
      ) : (
        <div className={styles.stepper}>
          {messages.map((message, i) => (
            <MessageContent
              key={i}
              message={message}
              toolResults={toolResults}
              isFirstModel={
                message.role == Role.model &&
                messages.findIndex((m) => m.role == Role.model) == i
              }
              isLastInGroup={i == messages.length - 1}
              isLastGroup={isLastGroup}
              isRequesting={isRequesting}
              width={width}
              showThoughts={showThoughts}
              onToolResponse={onToolResponse}
              onRetry={onRetry}
              onToggleShowThoughts={onToggleShowThoughts}
            />
          ))}
        </div>
      )}
    </div>
  );
});

interface MessagesProps {
  width: number;
}

const Messages = observer((props: MessagesProps) => {
  const { width } = props;
  const store = getStore().chat;

  const onToolResponse = (tool: ToolUse, response: unknown) => {
    store.sendToolResponse(tool, response);
  };

  const onRetry = (message: ChatMessage) => {
    store.retry(message);
  };

  const groups: ChatMessage[][] = [];
  for (const message of store.messages) {
    if (
      message.role == Role.user ||
      message.isUserSelection ||
      groups.length == 0 ||
      groups[groups.length - 1][0].role == Role.user ||
      groups[groups.length - 1][0].isUserSelection
    ) {
      groups.push([message]);
    } else {
      groups[groups.length - 1].push(message);
    }
  }

  return (
    <div className={styles.messages}>
      {groups.map((group, i) => (
        <MessageGroup
          key={i}
          messages={group}
          isLastGroup={i == groups.length - 1}
          isRequesting={store.isRequesting}
          width={width}
          showThoughts={store.showThoughts}
          onToolResponse={onToolResponse}
          onRetry={onRetry}
          onToggleShowThoughts={() => {
            store.showThoughts = !store.showThoughts;
          }}
        />
      ))}
    </div>
  );
});

export const Chat = observer((props: IDockviewPanelProps) => {
  const preferences = getStore().preferences;
  const store = getStore().chat;
  const [width, setWidth] = useState(props.api.width);
  const [showToS, setShowToS] = useState(false);

  useEffect(() => {
    const disposable = props.api.onDidDimensionsChange((e) => {
      setWidth(e.width);
    });
    return () => disposable.dispose();
  }, []);

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.ctrlKey || e.metaKey) && e.key == "Enter") {
      store.send();
    }
  };

  const onConsent = () => {
    setShowToS(false);
    preferences.bigqueryAICompletionToSConsent = true;
    preferences.bigqueryAICompletionEnabled = true;
  };

  const onEnableAIFunction = () => {
    if (!preferences.bigqueryAICompletionToSConsent) {
      setShowToS(true);
      return;
    }
    preferences.bigqueryAICompletionEnabled = true;
  };

  return preferences.bigqueryAICompletionEnabled ? (
    <div className={styles.root}>
      <Messages width={width} />
      <div>
        <Form.Control
          placeholder={I18n.t("bigquery_query_viewer.label.prompt_chat")}
          as="textarea"
          style={{
            resize: "none",
            borderWidth: "1px 0 0 0",
            borderRadius: 0,
          }}
          className={styles.promptTextArea}
          value={store.prompt}
          onChange={(e) => (store.prompt = e.currentTarget.value)}
          onKeyDown={onKeyDown}
          disabled={store.isPromptDisabled}
        />
      </div>
    </div>
  ) : (
    <>
      <div className={styles.root2}>
        <div className="text-center">
          <BsRobot fontSize={40} color="grey" className="mb-2" />
          <div>
            <Button size="sm" onClick={onEnableAIFunction}>
              {I18n.t("bigquery_query_viewer.label.enable_ai_features")}
            </Button>
          </div>
          <div
            className="text-muted mt-2"
            style={{ fontSize: "0.75em", overflowWrap: "anywhere" }}
          >
            {I18n.t("bigquery_query_viewer.messages.ai_features_desc_1")}
            <br />
            {I18n.t("bigquery_query_viewer.messages.ai_features_desc_2")}
          </div>
        </div>
      </div>
      <Modal show={showToS} onHide={() => setShowToS(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            {I18n.t(
              "bigquery_query_viewer.label.ai_completion_terms_of_service",
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ul>
            <li>
              {I18n.t(
                "bigquery_query_viewer.messages.ai_completion_terms_of_service_1",
              )}
            </li>
            <li>
              {I18n.t(
                "bigquery_query_viewer.messages.ai_completion_terms_of_service_2",
              )}
            </li>
            <li>
              {I18n.t(
                "bigquery_query_viewer.messages.ai_completion_terms_of_service_3",
              )}
            </li>
          </ul>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={() => setShowToS(false)}>
            {I18n.t("bigquery_query_viewer.label.disagree")}
          </Button>
          <Button variant="primary" onClick={onConsent}>
            {I18n.t("bigquery_query_viewer.label.agree")}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
});
